// src/pages/PrivacyPolicy.tsx
import React, { useEffect } from "react";

export default function PrivacyPolicy() {
  useEffect(() => {
  document.title = "Privacy Policy • BacklinkBanana";
}, []);

  return (
  <div className="max-w-4xl mx-auto px-4 py-10">
    <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
    <p className="mb-4">Last Updated: 31/05/2025</p>

    <p className="mb-4">
      Welcome to <strong>BacklinkBanana</strong> (<a href="https://backlinkbanana.com" className="text-blue-600 hover:underline">https://backlinkbanana.com</a>), operated from India. Your privacy is very important to us. This Policy explains:
    </p>
    <ul className="list-disc ml-6 mb-6">
      <li>What data we collect</li>
      <li>How and why we use it</li>
      <li>What third parties we share it with</li>
      <li>Your rights and choices</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">1. Information We Collect</h2>
    <p className="mb-4">
      We only collect and store your email address when you register or sign in via Google. We do <em>not</em> collect or store any other personal information (no names, no addresses, no payment card details). Payment processing is handled entirely by Razorpay; we never see or store your payment credentials.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">2. How We Use Your Information</h2>
    <ul className="list-disc ml-6 mb-4">
      <li><strong>Authentication & Account Management</strong> – We use your email (from Google) to uniquely identify you, maintain your account, and track any credits you purchase. We never store any passwords or payment card details ourselves.</li>
      <li><strong>Communication</strong> – We send you transactional emails (e.g., account setup confirmations). You can opt out of promotional messages by clicking “Unsubscribe” in any marketing email.</li>
      <li><strong>Service Improvement</strong> – We use Google Analytics (GA4) to collect aggregate, anonymized usage data (page views, session length, browser type, etc.) so we can improve BacklinkBanana. No personal identifiers from Google Analytics are tied to your account.</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">3. Cookies and Analytics</h2>
    <p className="mb-4">
      • <strong>Google Analytics (GA4)</strong> – We use GA4 to measure page views, session duration, bounce rate, and similar metrics. Cookies set by GA4 include <code>_ga</code>, <code>_gid</code>, and <code>_gcl_au</code>. These cookies are used only for aggregate reporting; we do not associate GA4 data with your email or any other personal information.<br />
      • <strong>Session Cookie</strong> – When you log in, our backend issues a secure JWT token (sent as a <code>HttpOnly</code>, <code>Secure</code> cookie). This allows you to stay authenticated without exposing your token in JavaScript.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">4. Data Security</h2>
    <p className="mb-4">
      We follow industry-standard security measures to protect your email address and any account-related data:
    </p>
    <ul className="list-disc ml-6 mb-4">
      <li>All traffic is served over HTTPS (TLS 1.2+).</li>
      <li>We rely on Google for authentication—no passwords are stored on our side.</li>
      <li>Your email is stored in an encrypted PostgreSQL database on our Hetzner server (91.99.127.192).</li>
      <li>Access to our production server requires SSH key and MFA.</li>
    </ul>
    <p className="mb-4">
      However, no method is 100% secure. By using our service, you agree that BacklinkBanana Tech Pvt. Ltd. cannot be held responsible for unauthorized access due to events beyond our control.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">5. Data Sharing</h2>
    <p className="mb-4">We do not sell or rent your personal information. We only share data in the following circumstances:</p>
    <ul className="list-disc ml-6 mb-4">
      <li>
        <strong>Law Enforcement or Legal Compliance</strong><br />
        If required by a court order, subpoena, or other legal process, we may share your email or account logs. We will notify you unless prohibited by law.
      </li>
      <li>
        <strong>Trusted Service Providers</strong><br />
        We share only the minimum necessary data with:
        <ul className="list-disc ml-6 mb-2">
          <li><strong>Hosting Provider (Hetzner)</strong> – to keep the site and database running.</li>
          <li><strong>Analytics Provider (Google Analytics)</strong> – to track aggregate usage.</li>
          <li><strong>Authentication</strong> – Google handles sign-in; we never share user credentials directly.</li>
        </ul>
      </li>
      <li>
        <strong>Payment Processor (Razorpay)</strong><br />
        All billing and payment details (cards, UPI, etc.) are handled by Razorpay. We never receive or store your payment credentials.
      </li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">6. APIs and Third-Party Services</h2>
    <p className="mb-4">
      Our service integrates with:
      <br />
      • Google Custom Search API<br />
      • Moz API<br />
      • Google Analytics (GA4)<br />
      • Razorpay (for payments)<br />
      These services may return inaccurate, outdated, or incomplete results. We are not responsible for the data quality or accuracy provided by these third parties.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">7. Your Rights</h2>
    <p className="mb-4">
      • <strong>Opt Out of Marketing Emails</strong> – Click “Unsubscribe” from any marketing email. We will stop sending promotional content, but you may still receive essential account notifications.
    </p>
    <p className="mb-4">
      • <strong>Cookie Preferences</strong> – You may disable cookies via your browser settings, but some functionality (e.g., staying logged in) may be affected.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">8. Changes to This Policy</h2>
    <p className="mb-4">
      We may update this Privacy Policy when:
    </p>
    <ul className="list-disc ml-6 mb-4">
      <li>We add new features (e.g., new third-party integrations).</li>
      <li>Google or Razorpay change their requirements.</li>
      <li>We improve security practices.</li>
    </ul>
    <p className="mb-4">
      When we do, the “Last Updated” date above will change. Continued use of BacklinkBanana after the update means you accept the revised terms.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">Contact</h2>
    <p className="mb-4">
      For any questions or requests regarding your data, please email us at: <br />
      <a href="mailto:support@backlinkbanana.com" className="text-blue-600 hover:underline">
        support@backlinkbanana.com
      </a>
    </p>
  </div>
);


}
